import { useState, useEffect, useCallback } from "react"
import {
  Search,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Trash2,
  RefreshCw,
  Save,
  Pencil,
} from "lucide-react"
import { Button } from "../components/ui/button"
import { API_ENDPOINTS } from "../config/api"
import { apiPost, apiPut, apiDelete, fetchWithAuth } from "../utils/api"
import { useLanguage } from "../i18n/LanguageContext"

function CaptainImage({ url, label, detail = false }) {
  const { t } = useLanguage()
  const [failed, setFailed] = useState(false)
  const imageClass = detail ? "captain-detail-image" : "captain-image-thumbnail"

  if (!url || failed) {
    return <div className={`${imageClass} captain-image-placeholder`}>{t("common.noPhoto")}</div>
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="captain-image-link"
      title={t("common.openImage", { label })}
    >
      <img
        src={url}
        alt={label}
        className={imageClass}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    </a>
  )
}

const getCaptainPresence = (captain) => {
  const explicit = captain?.is_online ?? captain?.online ?? captain?.online_status
  if (typeof explicit === "boolean") return explicit ? "online" : "offline"
  if (typeof explicit === "string") {
    const normalized = explicit.toLowerCase()
    if (["true", "1", "online", "connected"].includes(normalized)) return "online"
    if (["false", "0", "offline", "disconnected"].includes(normalized)) return "offline"
  }

  const lastSeen = captain?.last_seen || captain?.last_seen_at || captain?.last_location_at
  if (lastSeen) {
    const timestamp = new Date(lastSeen).getTime()
    if (!Number.isNaN(timestamp)) {
      return Date.now() - timestamp <= 90 * 1000 ? "online" : "offline"
    }
  }
  return "unknown"
}

const getCaptainLastSeen = (captain) =>
  captain?.last_seen ||
  captain?.last_seen_at ||
  captain?.last_location_at ||
  null

const getCaptainHeartbeat = (captain) => captain?.heartbeat_at || null

const getCaptainReliability = (captain) => {
  const presence = getCaptainPresence(captain)
  if (presence !== "online") return "offline"
  const latitude = Number(captain?.last_latitude ?? captain?.latitude)
  const longitude = Number(captain?.last_longitude ?? captain?.longitude)
  const hasGps = Number.isFinite(latitude) && Number.isFinite(longitude) && !(latitude === 0 && longitude === 0)
  if (!hasGps) return "online_no_gps"
  const lastLocation = captain?.last_location_at || captain?.last_seen || captain?.last_seen_at
  const timestamp = lastLocation ? new Date(lastLocation).getTime() : NaN
  if (!Number.isFinite(timestamp) || Date.now() - timestamp > 90 * 1000) return "gps_stale"
  return "gps_live"
}

export default function CaptainManagement() {
  const { t, translateStatus, translateVehicle, translateApiMessage, formatNumber } = useLanguage()
  const [captains, setCaptains] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [presenceFilter, setPresenceFilter] = useState("")
  const [presenceCounts, setPresenceCounts] = useState({ online: 0, offline: 0, unknown: 0 })
  const [reliabilityCounts, setReliabilityCounts] = useState({ gps_live: 0, gps_stale: 0, online_no_gps: 0, offline: 0 })
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [selectedCaptain, setSelectedCaptain] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null)
  const [editDraft, setEditDraft] = useState(null)
  const [savingSettings, setSavingSettings] = useState(false)
  const [settingsMessage, setSettingsMessage] = useState("")

  /* ================= FETCH CAPTAINS ================= */
  const fetchCaptains = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    setError("")

    const token = localStorage.getItem("admin_token")
    if (!token) {
      setError(t("errors.sessionExpired"))
      if (!silent) setLoading(false)
      return
    }

    try {
      let url = `${API_ENDPOINTS.CAPTAINS}?page=${page}&per_page=10`
      if (statusFilter) url += `&status=${statusFilter}`
      if (presenceFilter) url += `&presence=${presenceFilter}`
      if (searchTerm) url += `&search=${searchTerm}`

      const [res, countsRes] = await Promise.all([
        fetchWithAuth(url),
        fetchWithAuth(`${API_ENDPOINTS.CAPTAINS}?page=1&per_page=200`),
      ])

      const data = await res.json()
      if (!res.ok) {
        throw new Error(translateApiMessage(data.message, "errors.fetchCaptains"))
      }

      setCaptains(data.captains || [])
      setLastUpdatedAt(new Date())
      const pageSize = Number(data.per_page || 10)
      const calculatedPages = Math.ceil(Number(data.total || data.total_count || 0) / pageSize)
      setTotalPages(Math.max(1, Number(data.pages || calculatedPages || 1)))

      if (countsRes.ok) {
        const countsData = await countsRes.json()
        const counts = (countsData.captains || []).reduce((result, captain) => {
          const presence = getCaptainPresence(captain)
          result[presence] = (result[presence] || 0) + 1
          return result
        }, { online: 0, offline: 0, unknown: 0 })
        setPresenceCounts(counts)
        const reliability = (countsData.captains || []).reduce((result, captain) => {
          const category = getCaptainReliability(captain)
          result[category] = (result[category] || 0) + 1
          return result
        }, { gps_live: 0, gps_stale: 0, online_no_gps: 0, offline: 0 })
        setReliabilityCounts(reliability)
      }
    } catch (err) {
      setError(translateApiMessage(err.message, "errors.fetchCaptains"))
    } finally {
      if (!silent) setLoading(false)
    }
  }, [page, searchTerm, statusFilter, presenceFilter, t, translateApiMessage])

  useEffect(() => {
    fetchCaptains()
  }, [fetchCaptains])

  useEffect(() => {
    const refreshTimer = window.setInterval(() => fetchCaptains({ silent: true }), 30 * 1000)
    return () => window.clearInterval(refreshTimer)
  }, [fetchCaptains])

  const normalizeRates = (rates, fallbackRate) => {
    const list = Array.isArray(rates) ? rates : []
    if (list.length === 0) {
      return { Economy: { rate_per_km: Number(fallbackRate || 0), minimum_fare: 0, waiting_time_rate: 0, hourly_rate: 0 } }
    }
    return list.reduce((result, rate) => {
      result[rate.service_type] = {
        rate_per_km: rate.rate_per_km ?? 0,
        minimum_fare: rate.minimum_fare ?? 0,
        waiting_time_rate: rate.waiting_time_rate ?? 0,
        hourly_rate: rate.hourly_rate ?? 0,
      }
      return result
    }, {})
  }

  const openCaptain = async (captain) => {
    setSelectedCaptain(captain)
    setShowModal(true)
    setSettingsMessage("")
    setEditDraft({
      governorate: captain.governorate || "",
      vehicle_type: captain.vehicle_type || "",
      rates: normalizeRates(captain.rates, captain.rate_per_km),
    })
    try {
      const response = await fetchWithAuth(API_ENDPOINTS.CAPTAIN_ADMIN_SETTINGS(captain.captain_id))
      const data = await response.json()
      if (response.ok && data.captain) {
        setSelectedCaptain(data.captain)
        setEditDraft({
          governorate: data.captain.governorate || "",
          vehicle_type: data.captain.vehicle_type || "",
          rates: normalizeRates(data.rates, data.captain.rate_per_km),
        })
      }
    } catch (err) {
      setSettingsMessage(translateApiMessage(err.message, "captains.settingsLoadFailed"))
    }
  }

  const handleSaveSettings = async () => {
    if (!selectedCaptain || !editDraft) return
    setSavingSettings(true)
    setSettingsMessage("")
    try {
      const response = await apiPut(
        API_ENDPOINTS.CAPTAIN_ADMIN_SETTINGS(selectedCaptain.captain_id),
        editDraft,
        { Authorization: `Bearer ${getToken()}` }
      )
      const updated = response.captain || selectedCaptain
      setSelectedCaptain(updated)
      setEditDraft({
        governorate: updated.governorate || "",
        vehicle_type: updated.vehicle_type || "",
        rates: normalizeRates(response.rates, updated.rate_per_km),
      })
      setSettingsMessage(t("captains.settingsSaved"))
      await fetchCaptains({ silent: true })
    } catch (err) {
      setSettingsMessage(translateApiMessage(err.message, "captains.settingsSaveFailed"))
    } finally {
      setSavingSettings(false)
    }
  }

  /* ================= ACTIONS ================= */
  const getToken = () => {
    const token = localStorage.getItem("admin_token")
    if (!token) throw new Error(t("errors.sessionExpired"))
    return token
  }

  const handleApprove = async (id, name) => {
    if (!window.confirm(t("captains.confirmApprove", { name }))) return
    try {
      await apiPost(
        API_ENDPOINTS.CAPTAIN_APPROVE(id),
        {},
        {
          Authorization: `Bearer ${getToken()}`,
        }
      )
      fetchCaptains()
      setShowModal(false)
    } catch (err) {
      setError(translateApiMessage(err.message))
    }
  }

  const handleReject = async (id, name) => {
    if (!window.confirm(t("captains.confirmReject", { name }))) return
    try {
      await apiPost(
        API_ENDPOINTS.CAPTAIN_REJECT(id),
        {},
        {
          Authorization: `Bearer ${getToken()}`,
        }
      )
      fetchCaptains()
      setShowModal(false)
    } catch (err) {
      setError(translateApiMessage(err.message))
    }
  }

  const handleDeactivate = async (id, name) => {
    if (!window.confirm(t("captains.confirmDeactivate", { name }))) return
    try {
      await apiPost(
        API_ENDPOINTS.CAPTAIN_DEACTIVATE(id),
        {},
        {
          Authorization: `Bearer ${getToken()}`,
        }
      )
      fetchCaptains()
      setShowModal(false)
    } catch (err) {
      setError(translateApiMessage(err.message))
    }
  }

  const handleDelete = async (id, name) => {
    if (!window.confirm(t("captains.confirmDelete", { name }))) return
    try {
      await apiDelete(
        `${API_ENDPOINTS.CAPTAINS}/${id}`,
        {
          Authorization: `Bearer ${getToken()}`,
        }
      )
      fetchCaptains()
      setShowModal(false)
    } catch (err) {
      setError(translateApiMessage(err.message))
    }
  }

  /* ================= HELPERS ================= */
  const normalizeStatus = (status) => (status || "").toUpperCase()

  const getStatusColor = (status) => {
    switch (normalizeStatus(status)) {
      case "ACTIVE":
        return "bg-green-100 text-green-800"
      case "PENDING":
        return "bg-yellow-100 text-yellow-800"
      case "DEACTIVATED":
        return "bg-gray-500 text-white"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const getPresenceColor = (presence) => {
    if (presence === "online") return "bg-green-100 text-green-800"
    if (presence === "offline") return "bg-gray-100 text-gray-700"
    return "bg-yellow-100 text-yellow-800"
  }

  const getPresenceLabel = (presence) => {
    if (presence === "online") return t("common.online")
    if (presence === "offline") return t("common.offline")
    return t("common.unknown")
  }

  const getReliabilityColor = (category) => {
    if (category === "gps_live") return "bg-green-100 text-green-800"
    if (category === "gps_stale") return "bg-amber-100 text-amber-800"
    if (category === "online_no_gps") return "bg-red-100 text-red-800"
    return "bg-gray-100 text-gray-700"
  }

  /* ================= LOADING ================= */
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    )
  }

  /* ================= UI ================= */
  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <h1 className="management-title font-bold text-gray-800">{t("captains.title")}</h1>

      <div className="presence-summary-grid">
        {[
          ["online", "bg-green-100 text-green-800"],
          ["offline", "bg-gray-100 text-gray-700"],
          ["unknown", "bg-yellow-100 text-yellow-800"],
        ].map(([key, color]) => (
          <button
            type="button"
            key={key}
            className={`presence-summary-card ${presenceFilter === key ? "presence-summary-card-selected" : ""}`}
            onClick={() => { setPresenceFilter(presenceFilter === key ? "" : key); setPage(1) }}
          >
            <span className={`px-2 py-1 rounded-full text-xs ${color}`}>{t(`captains.${key}`)}</span>
            <strong>{formatNumber(presenceCounts[key] || 0)}</strong>
          </button>
        ))}
      </div>

      <section className="reliability-panel" aria-label={t("captains.reliabilityTitle")}>
        <div className="reliability-panel-heading">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">{t("captains.reliabilityTitle")}</h2>
            <p className="text-sm text-gray-500 mt-1">{t("captains.reliabilityHint")}</p>
          </div>
          <span className="text-xs text-gray-400">{t("captains.reliabilityWindow")}</span>
        </div>
        <div className="reliability-grid">
          {[
            ["gps_live", "bg-green-100 text-green-800"],
            ["gps_stale", "bg-amber-100 text-amber-800"],
            ["online_no_gps", "bg-red-100 text-red-800"],
            ["offline", "bg-gray-100 text-gray-700"],
          ].map(([key, color]) => (
            <div className="reliability-card" key={key}>
              <span className={`px-2 py-1 rounded-full text-xs ${color}`}>{t(`captains.${key}`)}</span>
              <strong>{formatNumber(reliabilityCounts[key] || 0)}</strong>
            </div>
          ))}
        </div>
      </section>

      <div className="flex items-center justify-between gap-3 text-sm text-gray-500">
        <span>
          {lastUpdatedAt
            ? t("captains.lastUpdated", { time: lastUpdatedAt.toLocaleTimeString() })
            : t("captains.statusUpdating")}
        </span>
        <Button
          type="button"
          onClick={() => fetchCaptains()}
          disabled={loading}
          className="bg-gray-100 hover:bg-gray-200 text-gray-700"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          {t("captains.refreshStatus")}
        </Button>
      </div>

      {/* SEARCH */}
      <div className="bg-white p-6 rounded-lg shadow">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            setPage(1)
            fetchCaptains()
          }}
          className="management-filter-form flex gap-3"
        >
          <div className="flex-1 relative">
            <Search className="search-icon-start absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("captains.searchPlaceholder")}
              className="search-input-with-icon w-full py-2 border rounded-lg"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
            className="management-filter-select px-4 py-2 border rounded-lg"
          >
            <option value="">{t("common.allStatuses")}</option>
            <option value="Pending">{t("status.pending")}</option>
            <option value="Active">{t("status.active")}</option>
            <option value="Deactivated">{t("status.deactivated")}</option>
          </select>

          <select
            value={presenceFilter}
            onChange={(e) => {
              setPresenceFilter(e.target.value)
              setPage(1)
            }}
            className="management-filter-select px-4 py-2 border rounded-lg"
          >
            <option value="">{t("captains.allPresence")}</option>
            <option value="online">{t("captains.online")}</option>
            <option value="offline">{t("captains.offline")}</option>
            <option value="unknown">{t("captains.unknown")}</option>
          </select>

          <Button type="submit" className="management-filter-submit bg-amber-600">
            {t("common.search")}
          </Button>
        </form>
      </div>

      {/* TABLE */}
      <div className="data-table-container bg-white rounded-lg shadow">
        <table className="data-table captain-table w-full">
          <thead className="bg-gray-50">
            <tr>
              {[
                t("common.name"),
                t("common.email"),
                t("common.phone"),
                t("captains.registrationLocation"),
                t("captains.profilePhoto"),
                t("captains.vehiclePhoto"),
                t("captains.vehicle"),
                t("captains.rating"),
                t("captains.presence"),
                t("captains.reliability"),
                t("captains.lastSeen"),
                t("captains.heartbeat"),
                t("common.status"),
                t("common.actions"),
              ].map((h) => (
                <th key={h} className="px-6 py-3 text-left text-xs uppercase whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {captains.length > 0 ? captains.map((c) => (
              <tr key={c.captain_id} className="border-t hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">{c.name}</td>
                <td className="px-6 py-4 whitespace-nowrap"><span className="bidi-isolate">{c.email}</span></td>
                <td className="px-6 py-4 whitespace-nowrap"><span className="bidi-isolate">{c.phone_number}</span></td>
                <td className="px-6 py-4 whitespace-nowrap">{c.registration_location || t("common.notAvailable")}</td>
                <td className="px-6 py-4">
                  <CaptainImage
                    url={c.profile_image_url}
                    label={t("captains.profilePhotoAlt", { name: c.name })}
                  />
                </td>
                <td className="px-6 py-4">
                  <CaptainImage
                    url={c.vehicle_image_url}
                    label={t("captains.vehiclePhotoAlt", { name: c.name })}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{translateVehicle(c.vehicle_type)}</td>
                <td className="px-6 py-4 whitespace-nowrap">⭐ {formatNumber(c.rating || 0, { maximumFractionDigits: 1 })}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {(() => {
                    const presence = getCaptainPresence(c)
                    return (
                      <span className={`px-3 py-1 rounded-full text-xs ${getPresenceColor(presence)}`}>
                        {getPresenceLabel(presence)}
                      </span>
                    )
                  })()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {(() => {
                    const reliability = getCaptainReliability(c)
                    return <span className={`px-3 py-1 rounded-full text-xs ${getReliabilityColor(reliability)}`}>{t(`captains.${reliability}`)}</span>
                  })()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {(() => {
                    const lastSeen = getCaptainLastSeen(c)
                    return lastSeen ? new Date(lastSeen).toLocaleString() : t("common.notAvailable")
                  })()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {(() => {
                    const heartbeat = getCaptainHeartbeat(c)
                    return heartbeat ? new Date(heartbeat).toLocaleString() : t("common.notAvailable")
                  })()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-3 py-1 rounded-full text-xs ${getStatusColor(
                      c.status
                    )}`}
                  >
                    {translateStatus(c.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => openCaptain(c)}
                    className="text-amber-600"
                  >
                    <Eye className="inline w-4 h-4" /> {t("common.view")}
                  </button>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="14" className="px-6 py-8 text-center text-gray-500">
                  {t("captains.noCaptains")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {t("common.pageOf", { page: formatNumber(page), total: formatNumber(totalPages) })}
        </p>
        <div className="flex gap-2">
          <Button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 disabled:opacity-50"
          >
            {t("common.previous")}
          </Button>
          <Button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 disabled:opacity-50"
          >
            {t("common.next")}
          </Button>
        </div>
      </div>

      {/* MODAL */}
      {showModal && selectedCaptain && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center modal-backdrop z-50">
          <div className="responsive-modal bg-white rounded-lg max-w-xl w-full overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">
              {selectedCaptain.name}
            </h2>

            <section className="mb-5">
              <h3 className="captain-image-title">{t("captains.approximateRegistrationLocation")}</h3>
              <p className="text-gray-800">{selectedCaptain.registration_location || t("common.notAvailable")}</p>
            </section>

            <div className="captain-detail-image-grid mb-5">
              <section>
                <h3 className="captain-image-title">{t("captains.profilePicture")}</h3>
                <CaptainImage
                  url={selectedCaptain.profile_image_url}
                  label={t("captains.profilePhotoAlt", { name: selectedCaptain.name })}
                  detail
                />
              </section>
              <section>
                <h3 className="captain-image-title">{t("captains.vehiclePhoto")}</h3>
                <CaptainImage
                  url={selectedCaptain.vehicle_image_url}
                  label={t("captains.vehiclePhotoAlt", { name: selectedCaptain.name })}
                  detail
                />
              </section>
            </div>

            {editDraft && (
              <section className="captain-settings-panel mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <Pencil className="w-4 h-4 text-amber-600" />
                  <h3 className="font-semibold text-gray-800">{t("captains.profileControls")}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  <label className="text-sm text-gray-700">
                    {t("captains.governorate")}
                    <select className="w-full mt-1 border rounded-lg p-2" value={editDraft.governorate} onChange={(e) => setEditDraft({ ...editDraft, governorate: e.target.value })}>
                      <option value="">{t("common.notSpecified")}</option>
                      {[["Cairo", "Cairo"], ["Giza", "Giza"], ["Asyut", "Asyut"], ["Red Sea", "Red Sea"], ["South Sinai", "South Sinai"], ["Alexandria", "Alexandria"]].map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                  <label className="text-sm text-gray-700">
                    {t("captains.vehicleType")}
                    <select className="w-full mt-1 border rounded-lg p-2" value={editDraft.vehicle_type} onChange={(e) => setEditDraft({ ...editDraft, vehicle_type: e.target.value })}>
                      {[["WithVehicle", t("vehicle.withVehicle")], ["Scooter", t("vehicle.scooter")], ["WithoutVehicle", t("vehicle.withoutVehicle")], ["No vehicle", t("vehicle.noVehicle")]].map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs"><thead><tr><th className="text-left p-2">{t("captains.service")}</th><th className="text-left p-2">{t("captains.ratePerKm")}</th><th className="text-left p-2">{t("captains.minimumFare")}</th><th className="text-left p-2">{t("captains.waitingRate")}</th><th className="text-left p-2">{t("captains.hourlyRate")}</th></tr></thead>
                    <tbody>{Object.entries(editDraft.rates).map(([service, fields]) => <tr key={service}><td className="p-2 font-medium">{service}</td>{["rate_per_km", "minimum_fare", "waiting_time_rate", "hourly_rate"].map((field) => <td className="p-2" key={field}><input type="number" min="0" step="0.01" className="w-24 border rounded p-1" value={fields[field] ?? 0} onChange={(e) => setEditDraft({ ...editDraft, rates: { ...editDraft.rates, [service]: { ...fields, [field]: e.target.value } } })} /></td>)}</tr>)}</tbody>
                  </table>
                </div>
                {settingsMessage && <p className="text-sm mt-3 text-amber-700">{settingsMessage}</p>}
                <Button type="button" onClick={handleSaveSettings} disabled={savingSettings} className="bg-amber-600 text-white mt-4"><Save className="w-4 h-4 mr-2" />{savingSettings ? t("captains.savingSettings") : t("captains.saveSettings")}</Button>
              </section>
            )}

            {normalizeStatus(selectedCaptain.status) === "PENDING" && (
              <div className="flex gap-4 mb-4">
                <Button
                  onClick={() =>
                    handleApprove(
                      selectedCaptain.captain_id,
                      selectedCaptain.name
                    )
                  }
                  className="bg-green-600 text-white flex-1"
                >
                  <CheckCircle className="w-4 h-4 mr-2" /> {t("captains.approve")}
                </Button>

                <Button
                  onClick={() =>
                    handleReject(
                      selectedCaptain.captain_id,
                      selectedCaptain.name
                    )
                  }
                  className="bg-red-600 text-white flex-1"
                >
                  <XCircle className="w-4 h-4 mr-2" /> {t("captains.reject")}
                </Button>
              </div>
            )}

            {normalizeStatus(selectedCaptain.status) === "DEACTIVATED" && (
              <Button
                onClick={() =>
                  handleApprove(
                    selectedCaptain.captain_id,
                    selectedCaptain.name
                  )
                }
                className="bg-green-600 text-white w-full mb-4"
              >
                {t("captains.activate")}
              </Button>
            )}

            {normalizeStatus(selectedCaptain.status) === "ACTIVE" && (
              <>
                <Button
                  onClick={() =>
                    handleDeactivate(
                      selectedCaptain.captain_id,
                      selectedCaptain.name
                    )
                  }
                  className="bg-yellow-600 text-white w-full mb-3"
                >
                  {t("captains.deactivate")}
                </Button>

                <Button
                  onClick={() =>
                    handleDelete(
                      selectedCaptain.captain_id,
                      selectedCaptain.name
                    )
                  }
                  className="bg-red-700 text-white w-full mb-4"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> {t("captains.delete")}
                </Button>
              </>
            )}

            <button
              onClick={() => setShowModal(false)}
              className="w-full bg-gray-200 py-2 rounded"
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
